// frontend/src/components/admin/tenants/ouTree/OuBranchNode.tsx
/**
 * Card colapsavel de 1 UO (depth=0: kind=tenant; depth>0: kind=sub_tenant).
 *
 * Layout 2-niveis:
 *  - Header sempre visivel: chevron, nome, badges (legado/inativo/SaaS),
 *    summary "N ambientes · PRD · HML · QA · DEV", status agregado, + e ⋮.
 *  - Body (Collapse): cada Sub-UO (kind=environment) vira um
 *    <SubOuCollapseGroup> independente — seu proprio chevron+pill+contas.
 *  - Quando ha kind=category name=SaaS, suas grandchildren kind=sub_tenant
 *    sao renderizadas como mini-cards <OuBranchNode depth+1>.
 *  - Quando ha AMBOS sets, sao separados por dividers de secao
 *    "AMBIENTES DIRETOS" e "SUB-TENANTS (N)".
 *
 * Recursao: a sub_tenant card chama <OuBranchNode> recursivamente. Limite
 * de profundidade depth<=2 — alem disso, sub-tenants sao achatados como
 * envs diretos (defensivo, nao deve ocorrer no schema real).
 *
 * Estado de expand/collapse:
 *  - matchesSearch=true forca open (busca tem prioridade)
 *  - expandedSet (gerenciado pelo orquestrador) guarda Set<ou_id>
 *    que tiveram seu estado togglado manualmente (inverte default).
 *  - default: depth < 2 = aberto, depth >= 2 = fechado.
 */
import { useState, useCallback, useMemo } from 'react'
import type { KeyboardEvent } from 'react'
import {
  Card,
  Group,
  Text,
  Badge,
  Menu,
  ActionIcon,
  Box,
  Collapse,
  Tooltip,
} from '@mantine/core'
import {
  IconChevronRight,
  IconAlertTriangle,
  IconBuilding,
  IconDots,
  IconPencil,
  IconPlus,
  IconShieldLock,
  IconUsers,
} from '@tabler/icons-react'
import { type EnvName } from '@/api/awsOrgProvisioning'
import { AddEnvironmentModal } from '../modals/AddEnvironmentModal'
import { RenameModal } from '../modals/RenameModal'
import { EnableSaasModal } from '../modals/EnableSaasModal'
import { AddSubTenantModal } from '../modals/AddSubTenantModal'
import { SubOuCollapseGroup } from './SubOuCollapseGroup'
import { colorForEnv, orderForEnv } from './envOrdering'
import {
  STATUS_COLORS,
  STATUS_LABELS,
  STATUS_DESCRIPTIONS,
  getLeafStatus,
  type LeafStatus,
} from './leafStatus'
import type { MergedTreeNode, OuTreeBranch } from './types'
import type { TenantRow } from '../TenantsTable'
import { envsVivos } from './envsVivos'

export const OU_EXPANDED_STORAGE_KEY = 'consoleai.tenants.ou_expanded'
const MAX_RECURSION_DEPTH = 2

type AggregateStatus = LeafStatus | 'mixed'

const AGG_STATUS_COLORS: Record<AggregateStatus, string> = {
  ...STATUS_COLORS,
  mixed: 'yellow',
}

const AGG_STATUS_LABELS: Record<AggregateStatus, string> = {
  ...STATUS_LABELS,
  mixed: 'Misto',
}

const AGG_STATUS_DESCRIPTIONS: Record<AggregateStatus, string> = {
  ...STATUS_DESCRIPTIONS,
  mixed: 'Contas em estados diferentes (mistura de ready/provisioning/failed/inactive).',
}

interface RowProps {
  onToggleField: (id: number, field: string, value: boolean) => void
  onToggleZabbix: (tenantId: number, enabled: boolean) => void
  onToggleGitlab: (tenantId: number, currentlyEnabled: boolean) => void
  onToggleWhatsApi: (tenantId: number, enabled: boolean) => void
  onUpdateTimezone?: (tenantId: number, timezone: string | null) => void
  onEditRegions?: (tenant: TenantRow) => void
  onProvisioningFailedClick?: (tenant: TenantRow) => void
  fieldPendingIds?: Set<number>
  whatsapiPendingIds?: Set<number>
  timezonePendingIds?: Set<number>
}

interface Props {
  branch: OuTreeBranch
  depth: number
  /**
   * Set de ou_ids que tiveram o estado de expand explicitamente togglado
   * (inverte o default). Gerenciado pelo TenantsOuTreeView via localStorage.
   */
  expandedSet: Set<string>
  /**
   * Callback acionado quando usuario clica no chevron. Caller atualiza
   * expandedSet + persiste no localStorage.
   */
  onToggleExpand: (ouId: string) => void
  tenantIdByOuId: Map<string, number>
  rowProps: RowProps
}

function getDefaultExpanded(
  branch: OuTreeBranch,
  depth: number,
  expandedSet: Set<string>,
): boolean {
  if (branch.matchesSearch) return true
  const ouId = String(branch.node.ou_id)
  const defaultForDepth = depth < 2
  const isToggled = expandedSet.has(ouId)
  return isToggled ? !defaultForDepth : defaultForDepth
}

function collectAccounts(node: MergedTreeNode): TenantRow[] {
  if (node.type === 'account') return [node.tenant]
  if (node.type === 'ou') return node.children.flatMap(collectAccounts)
  return []
}

/**
 * Classifica os filhos diretos de uma UO em buckets:
 *  - directEnvs:    sub-OUs kind=environment (renderizam como SubOuCollapseGroup)
 *  - saasCategory:  primeiro filho kind=category name=SaaS (ou null)
 *  - subTenants:    netos do saasCategory que sao kind=sub_tenant (mini-cards)
 *  - accountLeaves: contas direto na propria UO (raro mas possivel)
 */
function classifyChildren(branch: OuTreeBranch) {
  const directEnvs: OuTreeBranch[] = []
  let saasCategory: OuTreeBranch | null = null
  const subTenants: OuTreeBranch[] = []
  const accountLeaves: TenantRow[] = []

  for (const child of branch.children) {
    if (child.type === 'account') {
      accountLeaves.push(child.tenant)
      continue
    }
    if (child.type !== 'ou') continue
    const kind = child.node.kind
    const name = String(child.node.name ?? '').toLowerCase()

    if (kind === 'environment') {
      directEnvs.push(child)
      continue
    }
    if (kind === 'category' && name === 'saas') {
      saasCategory = child
      for (const grand of child.children) {
        if (grand.type === 'ou' && grand.node.kind === 'sub_tenant') {
          subTenants.push(grand)
        }
      }
      continue
    }
    // outros tipos (category nao-saas, sub_tenant solto, sem kind, ...)
    // tratamos como direct env se parece um env, senao ignoramos.
    if (kind === 'sub_tenant') {
      // sub_tenant solto (sem SaaS category) — agrupar como sub-tenant
      subTenants.push(child)
    }
  }

  // ordena directEnvs por env order canonica
  directEnvs.sort((a, b) => orderForEnv(a.node.name) - orderForEnv(b.node.name))
  // ordena subTenants alfabeticamente pt-BR
  subTenants.sort((a, b) =>
    a.node.name.localeCompare(b.node.name, 'pt-BR', { sensitivity: 'base' }),
  )

  return { directEnvs, saasCategory, subTenants, accountLeaves }
}

function aggregateStatus(accounts: TenantRow[]): AggregateStatus {
  if (accounts.length === 0) return 'unknown'
  const statuses = accounts.map(getLeafStatus)
  if (statuses.some((s) => s === 'failed')) return 'failed'
  if (statuses.some((s) => s === 'provisioning')) return 'provisioning'
  if (statuses.every((s) => s === 'inactive')) return 'inactive'
  if (statuses.every((s) => s === 'ready')) return 'ready'
  if (statuses.every((s) => s === 'unknown')) return 'unknown'
  return 'mixed'
}

export function OuBranchNode({
  branch,
  depth,
  expandedSet,
  onToggleExpand,
  tenantIdByOuId,
  rowProps,
}: Props) {
  const [addEnvOpen, setAddEnvOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [enableSaasOpen, setEnableSaasOpen] = useState(false)
  const [addSubOpen, setAddSubOpen] = useState(false)

  const expanded = getDefaultExpanded(branch, depth, expandedSet)
  const ouId = String(branch.node.ou_id)
  const isTenant = branch.node.kind === 'tenant'
  const isSubTenant = branch.node.kind === 'sub_tenant'
  // Lápide do soft-delete: a OU não existe mais na AWS (aws_ou_tree_service
  // marca, nunca apaga). Ver TASK-9974.
  const ouRemovidaDaAws = Boolean(branch.node.is_legacy)

  const { directEnvs, saasCategory, subTenants, accountLeaves } = useMemo(
    () => classifyChildren(branch),
    [branch],
  )
  const hasSaas = saasCategory !== null || subTenants.length > 0

  // OU removida da AWS NAO conta como ambiente provisionado — ela sobrevive no
  // espelho por decisao de desenho (soft-delete guarda historico). Ver envsVivos.
  const currentEnvs: EnvName[] = useMemo(
    () => envsVivos(directEnvs.map((c) => c.node)),
    [directEnvs],
  )

  const accounts = useMemo(() => collectAccounts(branch), [branch])

  // Pos-refactor tenant-first (v1.46+): a tenant-OU (TECPAG) nao tem mais
  // tenant_row proprio — so as contas filhas (TECPAG-DEV/HML/QA) tem. Pra
  // restaurar os botoes "+", "Habilitar SaaS" e "Adicionar sub-tenant" no
  // card da tenant-OU, usamos qualquer conta filha como representativa. O
  // backend (provisioning_service._resolve_tenant_root_ou v1.49+) sobe a
  // arvore de OUs ate achar a tenant root, entao essa indirecao eh segura.
  const ownTenantId = tenantIdByOuId.get(ouId)
  const representativeTenantId =
    ownTenantId ??
    accounts.find((a) => a.is_active)?.id ??
    accounts[0]?.id
  const tenantId = representativeTenantId
  const showMenu = (isTenant || isSubTenant) && tenantId !== undefined
  const aggStatus = useMemo(() => aggregateStatus(accounts), [accounts])
  const aggStatusColor = AGG_STATUS_COLORS[aggStatus]
  const aggStatusLabel = AGG_STATUS_LABELS[aggStatus]
  const isFailed = aggStatus === 'failed'
  const isAllInactive = accounts.length > 0 && accounts.every((a) => !a.is_active)

  const handleToggle = useCallback(() => {
    onToggleExpand(ouId)
  }, [onToggleExpand, ouId])

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onToggleExpand(ouId)
      }
    },
    [onToggleExpand, ouId],
  )

  // Decisao se renderiza secao headers — so quando AMBOS buckets existem
  const showSectionHeaders = directEnvs.length > 0 && subTenants.length > 0

  // Mini-card styling pra sub_tenants (depth > 0)
  const isMini = depth > 0
  const cardRadius = isMini ? 'sm' : 'md'
  const cardBorderColor = isFailed
    ? 'var(--mantine-color-red-light-hover)'
    : isMini
    ? 'var(--mantine-color-default-border)'
    : undefined

  return (
    <>
      <Card
        withBorder
        radius={cardRadius}
        p={0}
        data-testid={`ou-branch-${ouId}`}
        style={{
          overflow: 'hidden',
          borderColor: cardBorderColor,
        }}
      >
        <Box
          px="md"
          py={isMini ? 'sm' : 'md'}
          onClick={handleToggle}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Recolher' : 'Expandir'} ${branch.node.name}`}
          onKeyDown={handleKey}
          data-testid={`ou-branch-chevron-${ouId}`}
          style={{
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background 120ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--mantine-color-default-hover)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent'
          }}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group
              gap="md"
              wrap="nowrap"
              align="center"
              style={{ flex: 1, minWidth: 0 }}
            >
              <IconChevronRight
                size={isMini ? 14 : 16}
                style={{
                  transition: 'transform 200ms',
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  color: 'var(--mantine-color-dimmed)',
                  flexShrink: 0,
                }}
              />
              <IconBuilding
                size={isMini ? 14 : 18}
                style={{ flexShrink: 0 }}
              />
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Group gap="xs" align="center" wrap="wrap">
                  <Text fw={500} size={isMini ? 'sm' : 'md'}>
                    {branch.node.name}
                  </Text>
                  {accounts.length > 0 && (
                    <Badge variant="light" size="sm">
                      {accounts.length} {accounts.length === 1 ? 'conta' : 'contas'}
                    </Badge>
                  )}
                  {branch.node.kind && branch.node.kind !== 'tenant' && (
                    <Badge
                      size="xs"
                      variant="light"
                      color={colorForEnv(branch.node.name)}
                      style={{ textTransform: 'lowercase' }}
                    >
                      {branch.node.kind}
                    </Badge>
                  )}
                  {hasSaas && (
                    <Tooltip
                      label={
                        subTenants.length > 0
                          ? `${subTenants.length} sub-tenant${subTenants.length === 1 ? '' : 's'} (modelo SaaS)`
                          : 'SaaS habilitado (sem sub-tenants ainda)'
                      }
                      withArrow
                    >
                      <Badge
                        size="xs"
                        variant="light"
                        color="grape"
                        leftSection={<IconShieldLock size={9} />}
                        style={{ textTransform: 'none', cursor: 'help' }}
                        data-testid={`saas-badge-${ouId}`}
                      >
                        SaaS · {subTenants.length}
                      </Badge>
                    </Tooltip>
                  )}
                  {branch.node.is_legacy && (
                    <Tooltip
                      label="Esta UO não existe mais na AWS Organizations; a linha sobrevive no espelho local como histórico"
                      withArrow
                    >
                      <Badge
                        size="xs"
                        variant="default"
                        leftSection={<IconAlertTriangle size={9} />}
                        style={{ textTransform: 'none', cursor: 'help' }}
                      >
                        legado
                      </Badge>
                    </Tooltip>
                  )}
                  {isAllInactive && (
                    <Badge
                      size="xs"
                      variant="default"
                      color="gray"
                      style={{ textTransform: 'none' }}
                    >
                      inativo
                    </Badge>
                  )}
                </Group>
              </Box>
            </Group>

            <Group
              gap="xs"
              wrap="nowrap"
              onClick={(e) => e.stopPropagation()}
            >
              {accounts.length > 0 && (
                <Tooltip
                  label={AGG_STATUS_DESCRIPTIONS[aggStatus]}
                  multiline
                  withArrow
                  position="top"
                  maw={300}
                >
                  <Badge
                    color={aggStatusColor}
                    variant="light"
                    size="sm"
                    radius="sm"
                    data-testid={`ou-branch-status-${ouId}`}
                    styles={{
                      root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        cursor: 'help',
                      },
                    }}
                  >
                    {aggStatusLabel}
                  </Badge>
                </Tooltip>
              )}
              {showMenu && (
                // OU removida da AWS não pode oferecer "adicionar ambiente": o
                // job nasceria e falharia no `create_ou`, já com a AWS mexida.
                // Sem filho vivo, `currentEnvs` fica vazio e os quatro
                // checkboxes nasceriam habilitados — a tela pareceria
                // funcional. 7 das 29 OUs kind=tenant estão assim. TASK-9974.
                <Tooltip
                  label={
                    ouRemovidaDaAws
                      ? 'OU removida da AWS — recrie a estrutura do tenant antes'
                      : 'Adicionar ambiente'
                  }
                  withArrow
                >
                  <ActionIcon
                    variant="light"
                    color="blue"
                    size={isMini ? 'sm' : 'md'}
                    onClick={() => setAddEnvOpen(true)}
                    disabled={ouRemovidaDaAws}
                    aria-label="Adicionar ambiente"
                    data-testid={`ou-add-env-${ouId}`}
                  >
                    <IconPlus size={isMini ? 14 : 16} />
                  </ActionIcon>
                </Tooltip>
              )}
              {showMenu && (
                <Menu shadow="md" width={220} position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size={isMini ? 'sm' : 'md'}
                      aria-label="Ações do tenant"
                      data-testid={`ou-actions-${ouId}`}
                    >
                      <IconDots size={isMini ? 14 : 16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>{branch.node.name}</Menu.Label>
                    <Menu.Item
                      leftSection={<IconPlus size={14} />}
                      onClick={() => setAddEnvOpen(true)}
                    >
                      Adicionar ambiente
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconPencil size={14} />}
                      onClick={() => setRenameOpen(true)}
                    >
                      Renomear
                    </Menu.Item>
                    <Menu.Divider />
                    {!hasSaas && (
                      <Menu.Item
                        leftSection={<IconShieldLock size={14} />}
                        onClick={() => setEnableSaasOpen(true)}
                      >
                        Habilitar SaaS
                      </Menu.Item>
                    )}
                    {hasSaas && (
                      <Menu.Item
                        leftSection={<IconUsers size={14} />}
                        onClick={() => setAddSubOpen(true)}
                      >
                        Adicionar sub-tenant
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Group>
        </Box>

        <Collapse in={expanded}>
          <Box
            style={{
              borderTop: '1px solid var(--mantine-color-default-border)',
              background: isMini ? 'var(--mantine-color-default-hover)' : undefined,
            }}
          >
            <BodyContent
              depth={depth}
              directEnvs={directEnvs}
              subTenants={subTenants}
              accountLeaves={accountLeaves}
              showSectionHeaders={showSectionHeaders}
              expandedSet={expandedSet}
              onToggleExpand={onToggleExpand}
              tenantIdByOuId={tenantIdByOuId}
              parentTenantId={tenantId}
              parentExistingEnvs={currentEnvs}
              rowProps={rowProps}
            />
          </Box>
        </Collapse>
      </Card>

      {renameOpen && (
        <RenameModal
          opened={renameOpen}
          tipo="ou"
          ouId={ouId}
          nomeAtual={branch.node.name}
          onClose={() => setRenameOpen(false)}
        />
      )}
      {tenantId !== undefined && addEnvOpen && (
        <AddEnvironmentModal
          opened={addEnvOpen}
          onClose={() => setAddEnvOpen(false)}
          tenantId={tenantId}
          currentEnvs={currentEnvs}
          onSuccess={() => setAddEnvOpen(false)}
        />
      )}
      {tenantId !== undefined && enableSaasOpen && (
        <EnableSaasModal
          opened={enableSaasOpen}
          onClose={() => setEnableSaasOpen(false)}
          tenantId={tenantId}
          onSuccess={() => setEnableSaasOpen(false)}
        />
      )}
      {tenantId !== undefined && addSubOpen && (
        <AddSubTenantModal
          opened={addSubOpen}
          onClose={() => setAddSubOpen(false)}
          tenantId={tenantId}
          onSuccess={() => setAddSubOpen(false)}
        />
      )}
    </>
  )
}

interface BodyContentProps {
  depth: number
  directEnvs: OuTreeBranch[]
  subTenants: OuTreeBranch[]
  accountLeaves: TenantRow[]
  showSectionHeaders: boolean
  expandedSet: Set<string>
  onToggleExpand: (ouId: string) => void
  tenantIdByOuId: Map<string, number>
  parentTenantId?: number
  parentExistingEnvs: EnvName[]
  rowProps: RowProps
}

function BodyContent({
  depth,
  directEnvs,
  subTenants,
  accountLeaves,
  showSectionHeaders,
  expandedSet,
  onToggleExpand,
  tenantIdByOuId,
  parentTenantId,
  parentExistingEnvs,
  rowProps,
}: BodyContentProps) {
  // Empty: nem envs nem sub-tenants nem accounts
  if (
    directEnvs.length === 0 &&
    subTenants.length === 0 &&
    accountLeaves.length === 0
  ) {
    return (
      <Box px="md" py="sm">
        <Text size="xs" c="dimmed" fs="italic">
          Nenhum ambiente provisionado
        </Text>
      </Box>
    )
  }

  // Beyond max recursion depth: flatten sub_tenants ignoring nested SaaS
  // (mantem o user feedback que e infinito mas evita stack overflow)
  const allowSubTenantRecursion = depth < MAX_RECURSION_DEPTH

  return (
    <>
      {/* Conta solta direta na UO (raro — tenant sem Sub-UO de env) */}
      {accountLeaves.length > 0 &&
        accountLeaves.map((t) => {
          const envName = (t.environment || '?').toUpperCase()
          return (
            <SubOuCollapseGroup
              key={`leaf-${t.id}`}
              subOu={{
                id: `auto-${t.id}`,
                ou_id: `auto-${t.id}`,
                name: envName,
                parent_ou_id: null,
                kind: 'environment',
              }}
              accounts={[t]}
              expandedSet={expandedSet}
              onToggleExpand={onToggleExpand}
              parentTenantId={parentTenantId}
              parentExistingEnvs={parentExistingEnvs}
              rowProps={rowProps}
            />
          )
        })}

      {/* Secao AMBIENTES DIRETOS — so se ambos buckets existem */}
      {directEnvs.length > 0 && (
        <>
          {showSectionHeaders && (
            <SectionLabel testId="section-direct-envs">
              Ambientes diretos
            </SectionLabel>
          )}
          {directEnvs.map((env) => (
            <SubOuCollapseGroup
              key={`env-${env.node.ou_id}`}
              subOu={env.node}
              accounts={collectAccounts(env)}
              matchesSearch={env.matchesSearch}
              expandedSet={expandedSet}
              onToggleExpand={onToggleExpand}
              parentTenantId={parentTenantId}
              parentExistingEnvs={parentExistingEnvs}
              rowProps={rowProps}
            />
          ))}
        </>
      )}

      {/* Secao SUB-TENANTS — sempre rotulada se existem sub-tenants */}
      {subTenants.length > 0 && (
        <>
          <SectionLabel testId="section-sub-tenants">
            Sub-tenants ({subTenants.length})
          </SectionLabel>
          <Box p="sm" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {allowSubTenantRecursion ? (
              subTenants.map((sub) => (
                <OuBranchNode
                  key={`sub-${sub.node.ou_id}`}
                  branch={sub}
                  depth={depth + 1}
                  expandedSet={expandedSet}
                  onToggleExpand={onToggleExpand}
                  tenantIdByOuId={tenantIdByOuId}
                  rowProps={rowProps}
                />
              ))
            ) : (
              <Text size="xs" c="dimmed" fs="italic">
                Hierarquia muito profunda — abrir tenant individual para detalhes.
              </Text>
            )}
          </Box>
        </>
      )}
    </>
  )
}

function SectionLabel({
  children,
  testId,
}: {
  children: React.ReactNode
  testId: string
}) {
  return (
    <Box
      px="md"
      py={6}
      data-testid={testId}
      style={{
        background: 'var(--mantine-color-default-hover)',
        borderBottom: '1px solid var(--mantine-color-default-border)',
      }}
    >
      <Text
        size="xs"
        fw={700}
        c="dimmed"
        tt="uppercase"
        style={{ letterSpacing: 0.6 }}
      >
        {children}
      </Text>
    </Box>
  )
}

